import { AlertTriangle } from "lucide-react";
import PropTypes from "prop-types";
import React from "react";

/**
 * Error Boundary para capturar erros em componentes React
 * Evita que erro em um componente quebre toda a aplicação
 */
class ErrorBoundary extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			hasError: false,
			error: null,
			errorInfo: null,
		};
	}

	/**
	 * Atualiza estado quando erro é capturado
	 */
	static getDerivedStateFromError(error) {
		return {
			hasError: true,
			error,
		};
	}

	/**
	 * Log de erro para monitoramento
	 */
	componentDidCatch(error, errorInfo) {
		console.error("Error caught by ErrorBoundary:", error);
		console.error("Component stack:", errorInfo.componentStack);

		this.setState({
			errorInfo,
		});

		// TODO: Enviar para serviço de monitoramento (Sentry, LogRocket, etc)
		// if (process.env.NODE_ENV === 'production') {
		//   sendErrorToMonitoring(error, errorInfo);
		// }
	}

	/**
	 * Reseta erro e tenta renderizar novamente
	 */
	handleReset = () => {
		this.setState({
			hasError: false,
			error: null,
			errorInfo: null,
		});
	};

	render() {
		if (this.state.hasError) {
			return (
				<div className="min-h-screen bg-[#0B0F19] flex items-center justify-center p-6">
					<div className="bg-slate-900/50 border border-red-500/20 rounded-xl p-8 max-w-lg w-full text-center">
						{/* Ícone de erro */}
						<div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-500/10 border border-red-500/20 mb-4">
							<AlertTriangle
								className="w-8 h-8 text-red-400"
								aria-hidden="true"
							/>
						</div>
						{/* Mensagem principal */}
						<h1 className="text-2xl font-bold text-slate-200 mb-2">
							Algo deu errado
						</h1>
						<p className="text-slate-400 mb-6">
							Ocorreu um erro inesperado na aplicação. Por favor, recarregue a
							página ou tente novamente mais tarde.
						</p>
						{/* Mensagem de erro (apenas em desenvolvimento) */}
						{process.env.NODE_ENV === "development" && this.state.error && (
							<details className="mb-6 text-left">
								<summary className="cursor-pointer text-sm text-red-400 mb-2">
									Detalhes técnicos (desenvolvimento)
								</summary>
								<div className="bg-slate-950/50 border border-red-500/20 rounded-lg p-4 text-xs font-mono text-red-300 overflow-auto max-h-48">
									<p className="mb-2 font-semibold">
										{this.state.error.toString()}
									</p>
									{this.state.errorInfo && (
										<pre className="text-slate-400 whitespace-pre-wrap">
											{this.state.errorInfo.componentStack}
										</pre>
									)}
								</div>
							</details>
						)}
						{/* Ações */}
						<div className="flex gap-3 justify-center">
							<button
								type="button"
								onClick={() => window.location.reload()}
								className="bg-cyan-500 hover:bg-cyan-600 text-white px-6 py-2.5 rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 focus:ring-offset-slate-900"
							>
								Recarregar Página
							</button>

							<button
								type="button"
								onClick={this.handleReset}
								className="bg-slate-700 hover:bg-slate-600 text-slate-200 px-6 py-2.5 rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 focus:ring-offset-slate-900"
							>
								Tentar Novamente
							</button>
						</div>{" "}
					</div>
				</div>
			);
		}

		return this.props.children;
	}
}

ErrorBoundary.propTypes = {
	children: PropTypes.node.isRequired,
};

export default ErrorBoundary;
